import fs from 'fs'
import path from 'path'

import { listRemoteServices, uploadService } from '../../api/console-client'
import type { SkippedService } from '../../errors'
import {
  DeployFailedError,
  HttpError,
  NoServicesDirectoryError,
  NoServicesError,
  NotLoggedInError,
  NoWorkspaceError,
  TokenExpiredError,
} from '../../errors'
import type { AuthGap, StoredAuth } from '../../project/auth'
import { authGap, loadAuth } from '../../project/auth'
import type { OperationOptions } from '../types'
import { emitter } from '../types'
import type { DiscoveredService } from './discover-services'
import { discoverServices } from './discover-services'
import { computeServiceHash } from './hash'
import type { PublicFile } from './public-files'
import { collectPublicFiles } from './public-files'
import type { Selection } from './select'
import { selectServices } from './select'
import { createServiceZip } from './zip'

export type { DiscoveredService, DiscoveryResult } from './discover-services'
export { discoverServices } from './discover-services'
export { computeServiceHash } from './hash'
export type { PublicFile, PublicFilesResult } from './public-files'
export { collectPublicFiles } from './public-files'
export type { Selection } from './select'
export { selectServices } from './select'
export { createServiceZip } from './zip'

// Deploying: read the project, work out what has actually changed, and upload each changed service on its
// own.
//
// One service per upload is the whole point. The workspace keeps whatever was deployed before from anywhere
// else, so services authored in separate projects accumulate rather than replace one another.
//
// Every service is compared against the workspace before anything is packaged, so the run can say which are
// new and which merely moved — and can decline to re-upload one that is byte-for-byte already there. A
// service's `sourceHash` covers its folder and nothing else, which is what makes that comparison meaningful
// from any project.
//
// Nothing here prints and nothing here exits. A missing login is an error, not a prompt — the CLI passes
// `onAuthRequired` and turns it back into an offer, and everyone else gets something they can catch.

/** What deploying a service would do to the workspace. */
export type ServiceStatus = 'new' | 'modified' | 'same'

export interface PlannedService {
  service: DiscoveredService
  serviceId: string
  /** The hash this deploy would publish. */
  sourceHash: string
  status: ServiceStatus
}

export type DeployEvent =
  | { type: 'connecting'; serverUrl: string; workspace: Workspace }
  | { type: 'discovered'; planned: PlannedService[] }
  | { type: 'skipped'; skipped: SkippedService }
  | { type: 'selected'; planned: PlannedService[] }
  | { type: 'icon-missing'; serviceId: string; relativePath: string }
  | { type: 'deployed'; entry: DeployedService }
  | { type: 'unchanged'; entry: PlannedService }

export interface DeployOptions extends OperationOptions<DeployEvent>, Selection {
  /**
   * Called when the project has no usable credentials, before giving up.
   *
   * The CLI passes an interactive login here, which is why `flowrunner deploy` can offer to connect a
   * project mid-run. A caller that passes nothing gets NotLoggedInError or NoWorkspaceError thrown instead —
   * a library must not open a browser on someone's behalf.
   */
  onAuthRequired?: (gap: AuthGap) => Promise<void>

  /**
   * Called when the project has several services and the caller named none.
   *
   * Same bargain as `onAuthRequired`: the CLI turns it into a picker, and a caller that passes nothing gets
   * SelectionRequiredError to handle its own way. Receives the classified list, so a picker can say which
   * services would actually change.
   */
  onSelectionRequired?: (available: PlannedService[]) => Promise<Selection>

  /**
   * Upload a service even when the workspace already has exactly this code.
   *
   * The default is to skip it — the deploy would produce the version that is already active, at the cost of
   * shipping the whole package. Worth overriding when the stored package itself is in doubt.
   */
  force?: boolean
}

export interface Workspace {
  workspaceId: string
  workspaceName: string
}

export interface DeployedService {
  serviceId: string
  serviceName: string
  sourceHash: string
  packageBytes: number
  /** `new` the first time the workspace sees it, `modified` when it replaces a different version. */
  status: Exclude<ServiceStatus, 'same'>
}

export interface DeployResult {
  workspace: Workspace
  serverUrl: string
  /** Services actually uploaded. */
  services: DeployedService[]
  /** Selected, but the workspace already had exactly this code. */
  unchanged: PlannedService[]
  /** Folders under `services/` that were not deployable, and why. Empty on a clean run. */
  skipped: SkippedService[]
}

/**
 * Resolve the credentials, giving `onAuthRequired` one chance to fill a gap.
 *
 * Re-reads from disk afterwards rather than trusting the hook's return value: the login writes
 * `flowrunner.json` and the token file, and reading them back is what proves it actually happened.
 */
async function resolveAuth(projectRoot: string, onAuthRequired?: DeployOptions['onAuthRequired']): Promise<StoredAuth> {
  const stored = loadAuth(projectRoot)
  const gap = authGap(stored)

  if (!gap) {
    return stored as StoredAuth
  }

  if (!onAuthRequired) {
    throw errorForGap(gap)
  }

  await onAuthRequired(gap)

  const refreshed = loadAuth(projectRoot)
  const remaining = authGap(refreshed)

  if (remaining) {
    throw errorForGap(remaining)
  }

  return refreshed as StoredAuth
}

function errorForGap(gap: AuthGap): Error {
  return gap === 'not-logged-in' ? new NotLoggedInError() : new NoWorkspaceError()
}

export async function deploy(options: DeployOptions = {}): Promise<DeployResult> {
  const projectRoot = options.cwd ?? process.cwd()
  const emit = emitter(options)

  const { serverUrl, workspaceId, workspaceName, deployToken } = await resolveAuth(projectRoot, options.onAuthRequired)
  const credentials = { serverUrl, workspaceId, deployToken }
  const workspace = { workspaceId, workspaceName }

  if (!fs.existsSync(path.resolve(projectRoot, 'services'))) {
    throw new NoServicesDirectoryError()
  }

  emit({ type: 'connecting', serverUrl, workspace })

  const { services: discovered, skipped } = discoverServices(projectRoot)

  for (const entry of skipped) {
    emit({ type: 'skipped', skipped: entry })
  }

  if (discovered.length === 0) {
    throw new NoServicesError(skipped)
  }

  // Classified before anything is selected, so a picker can say which services would actually change.
  const planned = await plan(projectRoot, discovered, credentials)

  emit({ type: 'discovered', planned })

  const selected = await resolveSelection(planned, options)

  emit({ type: 'selected', planned: selected })

  const deployed: DeployedService[] = []
  const unchanged: PlannedService[] = []

  // Sequential, and each upload stands alone: if the third service fails, the first two are deployed and
  // stay deployed. There is no package-wide transaction to roll back any more.
  for (const entry of selected) {
    if (entry.status === 'same' && !options.force) {
      unchanged.push(entry)
      emit({ type: 'unchanged', entry })

      continue
    }

    const { service, sourceHash } = entry
    const { files: icons, missing } = collectPublicFiles([service], projectRoot)

    for (const icon of missing) {
      emit({ type: 'icon-missing', ...icon })
    }

    const definition = { sourceHash, service }
    const zipBuffer = await createServiceZip(definition, projectRoot, service.serviceId)
    const result = await upload(credentials, zipBuffer, definition, icons)

    // `same` only reaches here under --force, where re-publishing the active version is the point.
    const status = entry.status === 'new' ? 'new' : 'modified'
    const record: DeployedService = { ...result, packageBytes: zipBuffer.length, status }

    deployed.push(record)
    emit({ type: 'deployed', entry: record })
  }

  return { workspace, serverUrl, services: deployed, unchanged, skipped }
}

/**
 * What deploying each discovered service would do, judged against what the workspace has active.
 *
 * The comparison is on `sourceHash`, which covers the service folder alone — so "same" means the active
 * version is this exact code, whichever project put it there.
 */
async function plan(
  projectRoot: string,
  discovered: DiscoveredService[],
  credentials: { serverUrl: string; workspaceId: string; deployToken: string }
): Promise<PlannedService[]> {
  const remote = await guarded(() => listRemoteServices(credentials))
  const active = new Map(remote.map(service => [service.serviceId, service.sourceHash]))

  return Promise.all(discovered.map(async service => {
    const sourceHash = await computeServiceHash(projectRoot, service.serviceId)
    const deployedHash = active.get(service.serviceId)

    const status: ServiceStatus = deployedHash === undefined
      ? 'new'
      : deployedHash === sourceHash ? 'same' : 'modified'

    return { service, serviceId: service.serviceId, sourceHash, status }
  }))
}

async function resolveSelection(
  planned: PlannedService[],
  options: DeployOptions
): Promise<PlannedService[]> {
  try {
    return selectServices(planned, options)
  } catch (error) {
    if (!options.onSelectionRequired || (error as { code?: string }).code !== 'FR_SELECTION_REQUIRED') {
      throw error
    }

    // The hook answers the question this operation cannot ask; its answer goes back through the same
    // resolver so an unknown id is rejected identically however it arrived.
    return selectServices(planned, await options.onSelectionRequired(planned))
  }
}

/** One upload, with the transport's failures translated into the CLI's vocabulary. */
async function upload(
  credentials: { serverUrl: string; workspaceId: string; deployToken: string },
  zipBuffer: Buffer,
  definition: { sourceHash: string; service: DiscoveredService },
  icons: PublicFile[]
) {
  return guarded(() => uploadService(credentials, zipBuffer, definition, icons))
}

/** Translates a transport failure into the CLI's vocabulary. */
async function guarded<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    // The status is the tell, not the prose. A console that mentions "401" while rejecting a package for an
    // unrelated reason used to be read as an expired session.
    if (error instanceof HttpError) {
      if (error.status === 401 || error.body.includes('CLI_TOKEN_EXPIRED')) {
        throw new TokenExpiredError()
      }

      throw new DeployFailedError(error.message, { status: error.status, body: error.body })
    }

    throw error
  }
}
